<script lang="ts" context="module">
  /**
   * Props Convenience Type Export
   * ---------------------------------------------------------------------------
   */

  import type { ComponentProps } from "svelte";
  import Button from "./Button.svelte"; // self reference
  export type ButtonProps = ComponentProps<Button>;

  /**
   * Types
   * ---------------------------------------------------------------------------
   */

  export type ButtonVariant = "primary" | "secondary" | "pill" | "cta" | "icon";
  export type ButtonSize = "large" | "medium" | "small";
  export type ButtonColor = "white" | "black";
</script>

<script lang="ts">
  /**
   * Imports
   * ---------------------------------------------------------------------------
   */

  import Icon, { type IconProps } from "@elements/Icon.svelte";
  import { createEventDispatcher } from "svelte";
  import { openIFrameModal } from "../stores/modalStore.ts";
  import ButtonWrapper from "@components/buttons/ButtonWrapper.svelte"; // TODO: absorb this component
  import clsx from "clsx";
  import { twMerge } from "tailwind-merge";

  /**
   * Props
   * ---------------------------------------------------------------------------
   */

  export let text: string;
  export let href = "";
  export let openInModal = false;
  export let openInNewTab: boolean = false;
  export let shareData: ShareData | null = null;
  export let variant: ButtonVariant = "primary";
  export let size: ButtonSize = "large";
  export let leftIcon: IconProps | null = null;
  export let rightIcon: IconProps | null = null;
  export let color: ButtonColor = "black";
  export let onClick: ((props: { text: string }) => void) | null = null;
  export let isLoading: boolean = false;
  export let isDisabled: boolean = false;

  /**
   * Style selection
   * ---------------------------------------------------------------------------
   */

  const sizeStyles = {
    tiny: "py-1 text-body-small",
    small: "py-2 text-body-small",
    medium: "py-3 text-body-base",
    large:
      "py-4 hover:no-underline tracking-even-wider text-body-base font-bold inline-block",
  };
  const inverseStyles = {
    white: "bg-black",
    black: "bg-white",
  };
  const colors = {
    white: clsx(
      "text-white stroke-white disabled:stroke-white",
      !isDisabled && "hover:stroke-p300 hover:fill-p300 hover:text-p300",
    ),
    black: clsx(
      "text-black stroke-black",
      !isDisabled && "hover:stroke-p100 hover:fill-p100 hover:text-p100",
    ),
  };

  const fillColors = {
    white: "fill-white stroke-white ",
    black:
      "fill-black hover:fill-p300 disabled:fill-black stroke-white hover:fill-p300 hover:stroke-p300 hover:text-p300",
  };

  const variantStyles = {
    primary: twMerge(
      sizeStyles[size ?? "large"],
      "px-6 border-half rounded-md",
      inverseStyles[color],
      colors[color],
    ),
    secondary: twMerge(
      fillColors["white"],
      sizeStyles[size ?? "large"],
      `border-half rounded-md bg-n200/30 backdrop-blur-md text-white px-6`,
      !isDisabled &&
        "hover:bg-white hover:fill-p300 hover:stroke-p300 hover:text-p300",
    ),
    cta: twMerge(
      fillColors[color],
      colors[color],
      "hover:stroke-p300 hover:text-p300",
      `stroke-${color}`,
    ),
    nav: `bg-none border-none stroke-${color} text-${color} hover:stroke-p300 hover:text-p300 p-0 focus:text-p300`,
    icon: twMerge(
      fillColors[color],
      `stroke-${color} text-${color} hover:stroke-p300 hover:text-p300 inline-flex flex-col items-center border-none text-body-small font-light`,
    ),
    pill: twMerge(
      inverseStyles[color],
      colors[color],
      `px-4 py-1 rounded-full`,
    ),
  };

  /**
   * Functions
   * ---------------------------------------------------------------------------
   */

  async function handleClick(event: Event) {
    if (openInModal || onClick) {
      event.preventDefault();
    }

    if (openInModal) {
      openIFrameModal({ src: href, title: text });
    }

    if (!!shareData && navigator) {
      if (navigator.canShare && navigator.canShare(shareData)) {
        await navigator.share(shareData);
      }
    }

    if (onClick) {
      onClick({ text });
    }

    dispatch("click");
  }

  function whichElement() {
    if (shareData) {
      return "span";
    }
    if (href && !openInModal) {
      return "a";
    }
    return "button";
  }

  /**
   * Misc
   * ---------------------------------------------------------------------------
   */

  const dispatch = createEventDispatcher();

  let target = openInNewTab ? "_blank" : "_self";
</script>

<!-- Template ----------------------------------------------------------------->

<ButtonWrapper {shareData}>
  <!-- svelte-ignore a11y-no-static-element-interactions-->
  <svelte:element
    this={whichElement()}
    on:mouseover={() => dispatch("hover")}
    on:focus={() => dispatch("focus")}
    href={href && !openInModal ? href : undefined}
    on:click={handleClick}
    disabled={isDisabled}
    id={$$props.id}
    {target}
    class={clsx(
      $$props.class,
      "text-center inline-flex justify-center items-center transition-all duration-300 cursor-pointer",
      "disabled:opacity-50 disabled:cursor-not-allowed", // disabled
      variantStyles[variant],
    )}
  >
    {#if isLoading}
      <Icon class="-ml-1 mr-3 h-5 w-5" name="spinner" />
    {/if}
    {#if leftIcon?.name}
      <Icon
        class={clsx("inline-block me-1")}
        name={leftIcon.name}
        size={leftIcon.size}
      />
    {/if}
    {text}
    {#if rightIcon?.name}
      <Icon
        class={clsx("inline-block ms-1")}
        name={rightIcon.name}
        size={rightIcon.size}
      />
    {/if}
  </svelte:element>
</ButtonWrapper>
